// src/utils/plainText.js (s85)
//
// Coerces a frontmatter string to plain text for the surfaces that cannot
// render markdown: JSX text nodes, meta descriptions, card summaries, and
// anything that gets truncated.
//
// ── Why this exists ─────────────────────────────────────────────────────────
//
// s84 found six articles shipping `[title](url)` to the reader as literal
// text: `tldr.closing` was authored as markdown and interpolated into JSX.
// That was fixed at the render site by routing it through ReactMarkdown, and
// debt #23 was deliberately left open as a CLASS, because any other
// frontmatter string a component drops into JSX can fail the same way.
//
// s85's render sweep found the next one on its first run: `excerpt` on two
// blog posts, visible on the post, the blog index, and the homepage card.
//
// `excerpt` could not be fixed the way `closing` was. It is not only a JSX
// text node — `BlogPost.jsx` also feeds it to `useSeo` as the meta
// description, where a React element is meaningless and the markdown would
// have gone out in the SERP snippet; and `Homepage.jsx` truncates it at 140
// characters, which can cut a markdown link in half and produce `[fan
// stro…`. Both of those want a STRING, not a rendered node.
//
// So the contract is the other direction for this class of field: excerpt,
// description and metaDescription are plain text by definition. The authored
// data is kept clean, this function makes the render sites defensive, and
// `scripts/verify-plaintext-frontmatter.mjs` fails the build if markdown is
// reintroduced upstream. Three layers because the first two are silent when
// they fail — a stripped link looks exactly like a link that was never there.
//
// What it handles, in order (order matters — images before links, or the
// leading `!` survives):
//   ![alt](src)      → alt
//   [text](href)     → text
//   **bold** __b__   → bold
//   *em* _em_        → em
//   `code`           → code
//   ~~strike~~       → strike
//
// It deliberately does NOT touch bare URLs, HTML tags, or entities. Those
// have never appeared in this field and a broader regex is a broader blast
// radius on prose that is mostly em-dashes and apostrophes.

export function toPlainText(value) {
  if (value == null) return '';
  let s = String(value);
  s = s.replace(/!\[([^\]]*)\]\([^)\s]*(?:\s+"[^"]*")?\)/g, '$1');
  s = s.replace(/\[([^\]]+)\]\([^)\s]*(?:\s+"[^"]*")?\)/g, '$1');
  s = s.replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '$2');
  s = s.replace(/(\*|_)(?=\S)([^*_]*?\S)\1/g, '$2');
  s = s.replace(/`([^`]+)`/g, '$1');
  s = s.replace(/~~(?=\S)([\s\S]*?\S)~~/g, '$1');
  return s.replace(/\s+/g, ' ').trim();
}

// Truncate on a word boundary. `Homepage.jsx` was slicing at a fixed index,
// which can land mid-word as easily as mid-link.
export function truncatePlain(value, max = 140) {
  const s = toPlainText(value);
  if (s.length <= max) return s;
  const cut = s.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return (sp > max * 0.6 ? cut.slice(0, sp) : cut).replace(/[\s,;:.–—-]+$/, '') + '…';
}

export default toPlainText;
